import { OutputData } from '@editorjs/editorjs';
import { Typography, Box, Button } from '@mui/material';
import { Stack } from '@mui/system';
import { m } from 'framer-motion';
import dynamic from 'next/dynamic';
import React, { useState, useRef, useEffect } from 'react';

interface ExpandableContentProps {
  title: string;
  content: string | OutputData;
  isEditorContent?: boolean;
  isDark?: boolean;
}
const EditorBlock = dynamic(() => import('./EditorJs'), { ssr: false });

const ExpandableContent: React.FC<ExpandableContentProps> = ({
  title,
  content,
  isEditorContent = false,
  isDark,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [shouldShowButton, setShouldShowButton] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  console.log('isDark', isDark);

  // Check content height whenever content changes or after initial render
  useEffect(() => {
    const checkHeight = () => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const shouldShow = contentHeight > 100;
        setShouldShowButton(shouldShow);
      }
    };

    // Check immediately
    checkHeight();

    // Add resize listener to handle window size changes
    window.addEventListener('resize', checkHeight);

    // Also check after a short delay to handle dynamic content
    const timeoutId = setTimeout(checkHeight, 100);

    return () => {
      window.removeEventListener('resize', checkHeight);
      clearTimeout(timeoutId);
    };
  }, [content]);

  // Re-check height when expanded state changes
  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      const shouldShow = contentHeight > 100;
      setShouldShowButton(shouldShow);
    }
  }, [isExpanded]);

  const COLLAPSED_HEIGHT = '110px';
  const FADE_START_PERCENTAGE = 85;

  return (
    <Stack gap={1}>
      <Typography variant="h5" color={isDark ? '#000' : '#fff'}>
        {title}
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'relative' }}>
          <m.div
            initial={false}
            animate={{
              height: isExpanded ? 'auto' : COLLAPSED_HEIGHT,
            }}
            transition={{ duration: 0.3 }}
            style={{
              overflow: 'hidden',
              maskImage: !isExpanded
                ? `linear-gradient(to bottom, black ${FADE_START_PERCENTAGE}%, transparent 100%)`
                : 'none',
              WebkitMaskImage: !isExpanded
                ? `linear-gradient(to bottom, black ${FADE_START_PERCENTAGE}%, transparent 100%)`
                : 'none',
            }}
          >
            <Box ref={contentRef} color="#b8b8b8">
              {isEditorContent ? (
                <EditorBlock data={content as OutputData} holder="editorjs" isDark={isDark} />
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    lineHeight: 1.6,
                    pb: 3, // Space for fade effect
                    minHeight: COLLAPSED_HEIGHT, // Ensure minimum height
                  }}
                >
                  {content as string}
                </Typography>
              )}
            </Box>
          </m.div>
        </Box>

        {shouldShowButton && (
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              cursor: 'pointer',
              mt: 1,
              p: 0,
              color: '#FFD369',
              fontSize: '1rem',
              fontWeight: 500,
              display: 'block',
              userSelect: 'none',
              '&:hover': {
                opacity: 0.8,
                backgroundColor: 'transparent', // Keep same color on hover
              },
              '&:active': {
                backgroundColor: 'transparent', // Keep same color on hover
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent', // Keep same color on hover
              },
            }}
          >
            {isExpanded ? 'Show less' : 'Read more'}
          </Button>
        )}
      </Box>
    </Stack>
  );
};
export default ExpandableContent;
